import { Link } from 'react-router-dom';
import { LogoFullDark } from '../components/Logo';
import { MapPin, Mail, Facebook, Instagram, Shield } from 'lucide-react';

/** Inline X/Twitter logo — lucide's bird is now off-brand after the rename. */
function XIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
      className={className}
    >
      <path d="M18.244 2H21.5l-7.56 8.64L23 22h-6.72l-5.26-6.87L4.98 22H1.72l8.08-9.24L1 2h6.9l4.76 6.3L18.244 2Zm-1.18 18h1.86L7.02 4h-1.97l12.014 16Z" />
    </svg>
  );
}

/** Inline TikTok wordmark — lucide doesn't ship a TikTok glyph. */
function TikTokIcon({ className = 'w-5 h-5' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
      className={className}
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}

const SOCIAL_LINKS = [
  {
    key: 'facebook',
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61588872260772',
    Icon: Facebook,
    ready: true,
  },
  {
    key: 'instagram',
    label: 'Instagram',
    href: 'https://www.instagram.com/roomhoraofficial',
    Icon: Instagram,
    ready: true,
  },
  {
    key: 'x',
    label: 'X (formerly Twitter)',
    href: 'https://x.com/roomhora',
    Icon: XIcon,
    ready: true,
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    href: 'https://www.tiktok.com/@roomhora',
    Icon: TikTokIcon,
    ready: true,
  },
];

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="rh-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div data-testid="footer-logo">
              <LogoFullDark size="default" />
            </div>
            <p className="text-slate-300 text-sm md:text-sm lg:text-[15px] leading-relaxed font-light">
              Find rental properties worldwide with exact locations, transparent pricing, and direct owner connections.
            </p>
            <div className="flex gap-3" data-testid="footer-socials">
              {SOCIAL_LINKS.map(({ key, label, href, Icon, ready }) => (
                <a
                  key={key}
                  href={href}
                  {...(ready
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : { 'aria-disabled': 'true', onClick: (e) => e.preventDefault() })}
                  aria-label={ready ? `RoomHora on ${label}` : `${label} — coming soon`}
                  title={ready ? `Follow us on ${label}` : `${label} — coming soon`}
                  data-testid={`social-${key}`}
                  className={
                    'flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 ' +
                    'text-slate-400 transition-all duration-200 hover:text-white hover:border-accent ' +
                    'hover:bg-slate-800/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 ' +
                    (ready ? '' : 'opacity-55 cursor-not-allowed hover:text-slate-400 hover:border-slate-700 hover:bg-transparent')
                  }
                >
                  <Icon className="w-[18px] h-[18px]" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-base md:text-lg lg:text-lg font-bold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/search" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Search Properties
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  List Your Property
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Owner Dashboard
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Saved Properties
                </Link>
              </li>
            </ul>
          </div>

          {/* Property Types */}
          <div>
            <h4 className="font-serif text-base md:text-lg lg:text-lg font-bold mb-4 text-white">Property Types</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/search?type=apartment" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Apartments
                </Link>
              </li>
              <li>
                <Link to="/search?type=house" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Houses
                </Link>
              </li>
              <li>
                <Link to="/search?type=room" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Rooms
                </Link>
              </li>
              <li>
                <Link to="/search?type=studio" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Studios
                </Link>
              </li>
              <li>
                <Link to="/search?type=office" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  Office Spaces
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-base md:text-lg lg:text-lg font-bold mb-4 text-white">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent mt-0.5" />
                <span className="text-sm md:text-sm lg:text-[15px] text-slate-400">
                  Available Worldwide<br />
                  Find properties in any city
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent" />
                <a href="mailto:roomhora@gmail.com" className="text-sm md:text-sm lg:text-[15px] text-slate-400 hover:text-white transition-colors">
                  roomhora@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-10 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-300 text-sm md:text-sm lg:text-[15px]">
              © {new Date().getFullYear()} <span className="font-semibold text-white">RoomHora</span>. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-6 text-sm md:text-sm lg:text-[15px]">
              <Link to="/fair-housing" className="text-slate-400 hover:text-accent transition-colors font-medium">
                Fair Housing
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-accent transition-colors font-medium">
                Privacy
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-accent transition-colors font-medium">
                Terms
              </Link>
              <Link to="/cookies" className="text-slate-400 hover:text-accent transition-colors font-medium">
                Cookies
              </Link>
            </div>
          </div>
          <div className="mt-6 flex flex-col md:flex-row justify-between items-center gap-2">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-800 rounded text-xs text-slate-400 border border-slate-700">
                <Shield className="w-3.5 h-3.5" />
                <span>Equal Housing Opportunity</span>
              </div>
              <span className="text-xs text-slate-500">We support the Fair Housing Act</span>
            </div>
            <span className="text-xs md:text-xs lg:text-sm text-slate-500">RoomHora Inc.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
